import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Briefcase } from "lucide-react";
import ProjectCard from "@/components/ProjectCard";
import MyCompanies from "@/components/MyCompanies";
import Wallet from "@/components/Wallet";
import DashboardStatCards from "@/components/DashboardStatCards";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ my_projects: 0, my_proposals: 0, accepted_proposals: 0, earnings: 0 });
  const [myProjects, setMyProjects] = useState([]);
  const [assignedProjects, setAssignedProjects] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, mp, ap] = await Promise.all([
          api.get("/stats/overview"),
          api.get("/projects/mine"),
          api.get("/projects/assigned"),
        ]);
        setStats(s.data);
        setMyProjects(mp.data);
        setAssignedProjects(ap.data);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs tracking-widest uppercase text-primary font-bold mb-2">Dashboard</div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tight">
              Hola, {user?.name?.split(" ")[0]}
            </h1>
          </div>
          <Link to="/projects/new">
            <Button className="rounded-full h-11 px-6" data-testid="dashboard-post-project-btn">
              <Plus className="w-4 h-4 mr-2" /> Publicar proyecto
            </Button>
          </Link>
        </div>

        {/* Stats (clickable) */}
        <DashboardStatCards stats={stats} />

        {/* Wallet */}
        <div className="mb-14">
          <Wallet />
        </div>

        {/* My companies */}
        <div className="mb-14">
          <MyCompanies />
        </div>

        {/* Projects I'm working on (as freelancer) */}
        {assignedProjects.length > 0 && (
          <div className="mb-14" data-testid="dashboard-assigned-section">
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <h2 className="font-heading text-2xl font-semibold tracking-tight">Proyectos que estoy haciendo</h2>
                <p className="text-sm text-muted-foreground mt-1">Proyectos donde fuiste aceptado como freelancer.</p>
              </div>
              <span className="text-[10px] tracking-widest uppercase font-bold text-emerald-600 bg-emerald-500/10 px-2 py-1 rounded-sm">
                {assignedProjects.length} asignado{assignedProjects.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assignedProjects.map((p) => (
                <ProjectCard key={p.project_id} project={p} data-testid={`assigned-project-${p.project_id}`} />
              ))}
            </div>
          </div>
        )}

        {/* My projects */}
        <div className="mb-10">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-heading text-2xl font-semibold tracking-tight">Mis proyectos</h2>
            <Link to="/projects" className="text-sm text-muted-foreground hover:text-primary">Ver todos →</Link>
          </div>
          {myProjects.length === 0 ? (
            <div className="border border-dashed border-border rounded-lg p-12 text-center">
              <Briefcase className="w-6 h-6 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground mb-4">Aún no has publicado ningún proyecto.</p>
              <Link to="/projects/new">
                <Button className="rounded-full" data-testid="empty-post-project-btn">
                  <Plus className="w-4 h-4 mr-2" /> Publicar mi primer proyecto
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myProjects.map((p) => <ProjectCard key={p.project_id} project={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
